import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { SEO } from "../components/SEO";
import {
  Paintbrush, ShoppingCart, Share2, Clock, Search, RefreshCw
} from "lucide-react";
import { API, GARMENT_TYPES } from "../constants";

const SHAPES = {
  tshirt: "M 25,18 Q 22,18 15,22 L 8,35 L 18,38 L 22,28 L 22,78 Q 22,82 28,82 L 72,82 Q 78,82 78,78 L 78,28 L 82,38 L 92,35 L 85,22 Q 78,18 75,18 Q 70,18 62,16 Q 56,14 50,14 Q 44,14 38,16 Q 30,18 25,18 Z",
  hoodie: "M 22,20 Q 18,20 10,24 L 2,40 L 14,44 L 18,30 L 18,82 Q 18,86 24,86 L 76,86 Q 82,86 82,82 L 82,30 L 86,44 L 98,40 L 90,24 Q 82,20 78,20 Q 72,20 64,18 L 62,12 Q 56,8 50,8 Q 44,8 38,12 L 36,18 Q 28,20 22,20 Z",
  tank: "M 30,16 L 28,18 L 22,78 Q 22,82 28,82 L 72,82 Q 78,82 78,78 L 72,18 L 70,16 Q 64,14 58,12 Q 54,11 50,11 Q 46,11 42,12 Q 36,14 30,16 Z",
  polo: "M 25,18 Q 22,18 15,22 L 8,35 L 18,38 L 22,28 L 22,78 Q 22,82 28,82 L 72,82 Q 78,82 78,78 L 78,28 L 82,38 L 92,35 L 85,22 Q 78,18 75,18 Q 70,18 62,16 Q 56,14 50,14 Q 44,14 38,16 Q 30,18 25,18 Z",
  hat: "M 15,55 Q 15,35 30,25 Q 40,18 50,18 Q 60,18 70,25 Q 85,35 85,55 L 85,60 Q 50,62 15,60 Z",
};

const COLORS_MAP = {
  White: "#FFFFFF", Black: "#1a1a1a", Navy: "#1e3a5f", Red: "#c0392b",
  "Royal Blue": "#2980b9", Forest: "#27ae60", Charcoal: "#444444",
  Gray: "#95a5a6", Orange: "#e67e22", Maroon: "#7b241c",
  Purple: "#6c3483", Gold: "#d4ac0d", Pink: "#e91e8a",
};

const DesignLibrary = () => {
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [searched, setSearched] = useState(false);
  const navigate = useNavigate();

  const fetchDesigns = async () => {
    if (!email.trim()) { toast.error("Enter your email to find your designs"); return; }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/designs`, { params: { email: email.trim() } });
      setDesigns(data.designs || []);
      setSearched(true);
      if (data.designs?.length === 0) toast.info("No designs found for this email");
    } catch {
      toast.error("Failed to load designs");
    } finally {
      setLoading(false);
    }
  };

  const reorder = (design) => {
    const params = new URLSearchParams();
    if (design.garment_type) params.set("garment", design.garment_type);
    if (design.print_method) params.set("method", design.print_method);
    if (design.options?.category) params.set("cat", design.options.category);
    navigate(`/design-studio?${params.toString()}`);
  };

  const shareProof = async (designNumber) => {
    try {
      const { data } = await axios.post(`${API}/designs/${designNumber}/proof`, { design_number: designNumber });
      const proofUrl = `${window.location.origin}/proof/${data.proof_id}`;
      await navigator.clipboard.writeText(proofUrl);
      toast.success(`Proof link copied! ${data.proof_id}`);
    } catch {
      toast.error("Failed to create proof");
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="design-library">
      <SEO title="My Designs" description="View and reorder your saved designs" path="/my-designs" />

      <div className="text-center mb-8">
        <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-muted">Your Work</span>
        <h1 className="font-archivo text-4xl sm:text-5xl uppercase tracking-tight text-ink mb-2">My Designs</h1>
        <p className="font-inter text-sm text-muted">Find your saved designs and reorder in one click</p>
      </div>

      {/* Email lookup */}
      <Card className="border-0 shadow-md mb-6">
        <CardContent className="p-5">
          <div className="flex gap-3 max-w-lg mx-auto">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email address"
              className="flex-1"
              data-testid="design-email-input"
              onKeyDown={(e) => e.key === "Enter" && fetchDesigns()}
            />
            <Button onClick={fetchDesigns} disabled={loading} className="bg-gradient-to-r from-orange-500 to-red-600 text-white" data-testid="find-designs-btn">
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}
              Find Designs
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Design Grid */}
      {searched && designs.length === 0 && (
        <div className="text-center py-12">
          <Paintbrush className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-gray-700 mb-1">No designs found</h2>
          <p className="text-sm text-gray-500 mb-4">Start a new design in our Design Studio</p>
          <Button onClick={() => navigate("/design-studio")} className="bg-gradient-to-r from-orange-500 to-red-600 text-white">
            Open Design Studio
          </Button>
        </div>
      )}

      {designs.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {designs.map((d) => {
            const garmentInfo = GARMENT_TYPES[d.garment_type] || { name: d.product_name || "Custom" };
            const colorHex = COLORS_MAP[d.garment_color] || "#FFFFFF";
            const isDark = ["Black", "Navy", "Charcoal", "Forest", "Maroon", "Purple"].includes(d.garment_color);
            const shape = SHAPES[d.garment_type] || SHAPES.tshirt;

            return (
              <Card key={d.id || d.design_number} className="border-0 shadow-md hover:shadow-lg transition overflow-hidden" data-testid={`design-card-${d.design_number}`}>
                {/* Garment preview */}
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4" style={{ height: 160 }}>
                  <svg viewBox="0 0 100 100" className="h-full" style={{ filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.1))" }}>
                    <path d={shape} fill={colorHex} stroke={isDark ? "#555" : "#ddd"} strokeWidth="0.5" />
                    {d.artwork_url && (
                      <image href={d.artwork_url} x="30" y="25" width="40" height="35" preserveAspectRatio="xMidYMid meet" />
                    )}
                  </svg>
                </div>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Badge className="bg-orange-100 text-orange-700 text-[10px]" data-testid={`design-badge-${d.design_number}`}>{d.design_number}</Badge>
                    <span className="text-[10px] text-gray-400 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(d.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <h3 className="text-sm font-bold text-gray-900 mb-0.5">{d.product_name || garmentInfo.name}</h3>
                  <p className="text-xs text-gray-500 mb-3">
                    {d.garment_color && `${d.garment_color} · `}{d.placement && `${d.placement} · `}Qty {d.quantity}
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <Button size="sm" onClick={() => reorder(d)} className="bg-gradient-to-r from-orange-500 to-red-600 text-white text-xs h-8" data-testid={`reorder-${d.design_number}`}>
                      <ShoppingCart className="w-3.5 h-3.5 mr-1" /> Reorder
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => shareProof(d.design_number)} className="text-xs h-8 border-gray-300" data-testid={`share-${d.design_number}`}>
                      <Share2 className="w-3.5 h-3.5 mr-1" /> Share Proof
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DesignLibrary;
